import { useContext, useMemo } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { buildInvitationLog, type InvitationLogFields, type InvitationLogStep } from '@bingads-webui-campaign-react/accept-invitation-api';

type Logger = {
  // eslint-disable-next-line no-unused-vars
  trace: (message: string) => void;
};

const noopLogger: Logger = {
  trace: () => {},
};

export type LogInvitationEvent = (
  // eslint-disable-next-line no-unused-vars
  step: InvitationLogStep,
  // eslint-disable-next-line no-unused-vars
  outcome: string,
  // eslint-disable-next-line no-unused-vars
  options?: { errorCode?: string; extra?: InvitationLogFields['extra'] }
) => void;

// Post-auth accept-invitation telemetry logger. Reads the shared instrumentation from context
// (the same pipeline that stamps every line with the telemetry session id / `SesID`), creates the
// dedicated `pmaxLiteAcceptInvitation` scenario logger, and returns a `logEvent` bound to the
// invitation `code` so every emitted line carries `code=<invitationCode>`.
//
// The lines follow the shared `InvitationIssues::Result step=… outcome=… errorCode=… code=…`
// schema (see accept-invitation-api/invitation-log), mirroring the server-side raw logs so the
// success-rate and error-count reports parse client + server the same way. Falls back to a no-op
// logger when instrumentation is unavailable (e.g. tests, demos) so callers never need to guard.
//
// NOTE: we ALWAYS emit via `trace` — never `error`. The monitoring system raises alerts on
// `error` traces, but these lines (including the error outcomes) are routine analytics for the
// success-rate / error-count reports, not actionable failures. Error cases are identified from the
// `outcome` / `errorCode` fields in the line, not from the log level.
const useTraceLogger = (): Logger => {
  const instrumentation = useContext(InstrumentationContext) as any;

  return useMemo<Logger>(() => {
    try {
      if (instrumentation && instrumentation.pmaxLiteAcceptInvitation) {
        return instrumentation.pmaxLiteAcceptInvitation.create().acceptInvitation.create();
      }
    } catch {
      // Telemetry initialization must not prevent the page or form from rendering.
    }
    return noopLogger;
  }, [instrumentation]);
};

export const useAcceptInvitationLogger = (code: string): LogInvitationEvent => {
  const logger = useTraceLogger();

  return useMemo<LogInvitationEvent>(() => (step, outcome, options = {}) => {
    logger.trace(buildInvitationLog({
      step,
      outcome,
      errorCode: options.errorCode,
      code,
      extra: options.extra,
    }));
  }, [logger, code]);
};

// Intentionally separate from InvitationIssues::Result: these events must never inflate its
// funnel denominator. Callers supply only fixed field names/categories, never form values or IDs.
export type InvitationFormDiagnostic = {
  event: 'SubmitAttempt' | 'ValidationRejected' | 'ActivateRequestStarted' | 'DraftRestored' | 'DraftDiscarded';
  reason?: string;
  fieldCount?: number;
  fields?: string;
  reasons?: string;
};

export const useInvitationFormLogger = (debug: boolean, mode: 'Regular' | 'Multiuser' | 'ConsentOnly') => {
  const logger = useTraceLogger();
  return useMemo(() => (diagnostic: InvitationFormDiagnostic) => {
    if (debug) return;
    const {
      event, reason, fieldCount, fields, reasons,
    } = diagnostic;
    const details = Object.entries({
      reason, fieldCount, fields, reasons,
    })
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join(' ');
    try {
      logger.trace(`InvitationForm::${event} mode=${mode}${details ? ` ${details}` : ''}`);
    } catch {
      // Diagnostics are best effort: never block validation, draft restore, or activation.
    }
  }, [logger, debug, mode]);
};
